<template>
  <div class="vx-row">
    <!-- Coluna principal -->
    <div class="vx-col lg:w-3/5 w-full">
      <vx-card class="mb-4">
        <h4 class="mb-1">{{ $t('fee_plans.plan_data') }}</h4>
        <small class="text-muted d-block mb-3">{{ $t('fee_plans.plan_data_hint') }}</small>

        <div class="vx-row">
          <div class="vx-col sm:w-1/2 w-full">
            <b-form-group :label="$t('fee_plans.name')" label-for="name">
              <b-form-input id="name" v-model="model.name" class="required" type="text"/>
            </b-form-group>
          </div>
          <div class="vx-col sm:w-1/2 w-full">
            <b-form-group :label="$t('fee_plans.description')" label-for="description">
              <b-form-input id="description" v-model="model.description" type="text"/>
            </b-form-group>
          </div>
        </div>
        <b-alert v-if="model.is_default" show variant="info" class="p-1 mb-0">
          <small>{{ $t('fee_plans.default_hint') }}</small>
        </b-alert>
      </vx-card>

      <!-- Regras por escopo -->
      <vx-card class="mb-4">
        <div class="flex justify-between items-center mb-4">
          <div>
            <h4 class="mb-0">{{ $t('fee_plans.rules') }}</h4>
            <small class="text-muted">{{ $t('fee_plans.rules_hint') }}</small>
          </div>
          <b-button variant="flat-primary" size="sm" @click="addRule">
            <feather-icon icon="PlusIcon" class="mr-50"/>
            <span class="align-middle">{{ $t('fee_plans.add_rule') }}</span>
          </b-button>
        </div>

        <div
          v-for="(rule, i) in version.rules"
          :key="i"
          class="rule-row border rounded p-1 mb-2">
          <div class="vx-row items-end">
            <div class="vx-col sm:w-3/12 w-full">
              <b-form-group :label="$t('fee_plans.scope')" label-size="sm">
                <b-form-select v-model="rule.scope_type" :options="scopeOptions" size="sm" :disabled="rule.scope_type === 'BASE'"/>
              </b-form-group>
            </div>

            <div v-if="rule.scope_type === 'PRODUCT_TYPE'" class="vx-col sm:w-3/12 w-full">
              <b-form-group :label="$t('fee_plans.product_type')" label-size="sm">
                <b-form-select v-model="rule.product_type" :options="productTypeOptions" size="sm"/>
              </b-form-group>
            </div>
            <div v-else-if="rule.scope_type === 'MODALITY'" class="vx-col sm:w-3/12 w-full">
              <b-form-group :label="$t('fee_plans.modality')" label-size="sm">
                <b-form-select v-model="rule.modality_id" :options="modalityOptions" size="sm"/>
              </b-form-group>
            </div>
            <div v-else-if="rule.scope_type === 'PRODUCT'" class="vx-col sm:w-3/12 w-full">
              <b-form-group :label="$t('fee_plans.product')" label-size="sm">
                <b-form-select v-model="rule.product_id" :options="productOptions" size="sm"/>
              </b-form-group>
            </div>
            <div v-else class="vx-col sm:w-3/12 w-full">
              <small class="text-muted d-block pb-2">{{ $t('fee_plans.base_scope_hint') }}</small>
            </div>

            <div class="vx-col sm:w-2/12 w-full">
              <b-form-group :label="$t('fee_plans.pricing_mode')" label-size="sm">
                <b-form-select v-model="rule.pricing_mode" :options="pricingModeOptions" size="sm"/>
              </b-form-group>
            </div>

            <template v-if="rule.pricing_mode === 'FLAT'">
              <div class="vx-col sm:w-2/12 w-full">
                <b-form-group :label="$t('fee_plans.percent')" label-size="sm">
                  <b-input-group append="%" size="sm">
                    <b-form-input v-model.number="rule.fee_percent" type="number" min="0" max="100" step="0.01" size="sm"/>
                  </b-input-group>
                </b-form-group>
              </div>
              <div class="vx-col sm:w-2/12 w-full">
                <b-form-group :label="$t('fee_plans.fixed')" label-size="sm">
                  <b-input-group prepend="R$" size="sm">
                    <b-form-input v-model.number="rule.fee_fixed" type="number" min="0" step="0.01" size="sm"/>
                  </b-input-group>
                </b-form-group>
              </div>
            </template>

            <div class="vx-col sm:w-1/12 w-full pb-2 ml-auto">
              <feather-icon
                v-if="rule.scope_type !== 'BASE'"
                icon="Trash2Icon"
                class="h-5 w-5 stroke-current text-danger cursor-pointer"
                @click="removeRule(i)"/>
            </div>
          </div>

          <!-- Faixas de volume da regra TIERED -->
          <div v-if="rule.pricing_mode === 'TIERED'" class="pl-2 pt-1 border-top">
            <div class="flex justify-between items-center mb-1">
              <small class="font-medium">{{ $t('fee_plans.tiers') }}</small>
              <b-button variant="flat-primary" size="sm" @click="addTier(rule)">
                <feather-icon icon="PlusIcon" class="mr-50"/>
                <span class="align-middle">{{ $t('fee_plans.add_tier') }}</span>
              </b-button>
            </div>
            <div
              v-for="(tier, j) in rule.tiers"
              :key="j"
              class="vx-row items-end mb-1">
              <div class="vx-col sm:w-3/12 w-full">
                <b-form-group :label="$t('fee_plans.tier_min_gmv')" label-size="sm">
                  <b-input-group prepend="R$" size="sm">
                    <b-form-input
                      v-model.number="tier.min_gmv"
                      type="number" min="0" step="0.01" size="sm"
                      :disabled="j === 0"
                      :placeholder="j === 0 ? $t('fee_plans.tier_floor') : ''"/>
                  </b-input-group>
                </b-form-group>
              </div>
              <div class="vx-col sm:w-3/12 w-full">
                <b-form-group :label="$t('fee_plans.tier_min_sales')" label-size="sm">
                  <b-form-input
                    v-model.number="tier.min_sales_count"
                    type="number" min="0" size="sm"
                    :disabled="j === 0"
                    :placeholder="j === 0 ? $t('fee_plans.tier_floor') : ''"/>
                </b-form-group>
              </div>
              <div class="vx-col sm:w-2/12 w-full">
                <b-form-group :label="$t('fee_plans.percent')" label-size="sm">
                  <b-input-group append="%" size="sm">
                    <b-form-input v-model.number="tier.fee_percent" type="number" min="0" max="100" step="0.01" size="sm"/>
                  </b-input-group>
                </b-form-group>
              </div>
              <div class="vx-col sm:w-2/12 w-full">
                <b-form-group :label="$t('fee_plans.fixed')" label-size="sm">
                  <b-input-group prepend="R$" size="sm">
                    <b-form-input v-model.number="tier.fee_fixed" type="number" min="0" step="0.01" size="sm"/>
                  </b-input-group>
                </b-form-group>
              </div>
              <div class="vx-col sm:w-1/12 w-full pb-2">
                <feather-icon
                  v-if="j > 0"
                  icon="Trash2Icon"
                  class="h-5 w-5 stroke-current text-danger cursor-pointer"
                  @click="rule.tiers.splice(j, 1)"/>
              </div>
            </div>
          </div>
        </div>
      </vx-card>
    </div>

    <!-- Coluna lateral -->
    <div class="vx-col lg:w-2/5 w-full">
      <vx-card class="mb-4">
        <h4 class="mb-3">{{ $t('fee_plans.seller_preview') }}</h4>
        <div class="border rounded p-1 mb-3">
          <small class="font-medium d-block mb-1">{{ $t('fee_plans.seller_preview_hint') }}</small>
          <small class="text-muted" style="white-space: pre-line">{{ sellerPreview }}</small>
        </div>
        <b-alert show variant="warning" class="p-1 mb-3">
          <div class="flex items-start">
            <feather-icon icon="GitBranchIcon" class="mr-1 flex-shrink-0"/>
            <small>{{ $t('fee_plans.publish_warning', { version: 'v' + version.version }) }}</small>
          </div>
        </b-alert>
        <b-form-group :label="$t('fee_plans.effective_from')" label-size="sm">
          <b-form-input v-model="publishEffectiveFrom" type="date"/>
        </b-form-group>
        <div class="flex gap-4">
          <b-button variant="primary" class="mr-2" :disabled="!model.name" @click="publish">
            {{ $t('fee_plans.publish') }} v{{ version.version }}
          </b-button>
          <b-button variant="outline-secondary" :disabled="!model.name" @click="saveDraft">
            {{ $t('fee_plans.save_draft') }}
          </b-button>
        </div>
      </vx-card>

      <vx-card>
        <h4 class="mb-3">{{ $t('fee_plans.simulator') }}</h4>
        <div class="vx-row">
          <div class="vx-col sm:w-1/2 w-full">
            <b-form-group :label="$t('fee_plans.simulator_amount')">
              <b-input-group prepend="R$">
                <b-form-input v-model.number="simulator.amount" type="number" min="0" step="0.01" @change="simulate"/>
              </b-input-group>
            </b-form-group>
          </div>
          <div class="vx-col sm:w-1/2 w-full">
            <b-form-group :label="$t('fee_plans.product_type')">
              <b-form-select v-model="simulator.product_type" :options="simulatorTypeOptions" @change="simulate"/>
            </b-form-group>
          </div>
        </div>
        <div class="vx-row">
          <div class="vx-col sm:w-1/2 w-full">
            <b-form-group :label="$t('fee_plans.simulator_gmv')">
              <b-input-group prepend="R$">
                <b-form-input v-model.number="simulator.period_gmv" type="number" min="0" @change="simulate"/>
              </b-input-group>
            </b-form-group>
          </div>
          <div class="vx-col sm:w-1/2 w-full">
            <b-form-group :label="$t('fee_plans.simulator_sales')">
              <b-form-input v-model.number="simulator.period_sales" type="number" min="0" @change="simulate"/>
            </b-form-group>
          </div>
        </div>

        <div v-if="simulator.result" class="border-top pt-3">
          <div class="flex justify-between mb-1">
            <small class="text-muted">{{ $t('fee_plans.simulator_rule') }}</small>
            <small>{{ $t('fee_plans.scope_.' + simulator.result.scope) }}</small>
          </div>
          <div class="flex justify-between mb-1">
            <small class="text-muted">{{ $t('fee_plans.simulator_fee') }}</small>
            <small class="hb-money">
              {{ formatCents(simulator.result.fee_amount_cents) }}
              ({{ simulator.result.fee_percent }}%<span v-if="simulator.result.fee_fixed_cents"> + {{ formatCents(simulator.result.fee_fixed_cents) }}</span>)
            </small>
          </div>
          <div class="flex justify-between">
            <small class="text-muted">{{ $t('fee_plans.simulator_net') }}</small>
            <small class="font-medium hb-money">{{ formatCents(simulator.result.net_amount_cents) }}</small>
          </div>
        </div>
      </vx-card>
    </div>
  </div>
</template>

<script>
import {
  BAlert,
  BButton,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BInputGroup
} from 'bootstrap-vue'
import { currencyFormatter } from '@/util/Util'

import FeePlanService from '@/services/api/FeePlanService'
import FeePlanVersionService from '@/services/api/FeePlanVersionService'
import ModalityService from '@/services/api/ModalityService'
import ProductService from '@/services/api/ProductService'

const PRODUCT_TYPE_LABELS = { LES: 'Aula', RSP: 'Espaço', SPL: 'Plano de assinatura' }

export default {
  components: {
    BAlert,
    BButton,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BInputGroup
  },

  props: {
    id: {
      type: Number,
      default: null
    }
  },

  data: () => ({
    service: null,
    versionService: null,
    modalityService: null,
    productService: null,
    modalityOptions: [],
    productOptions: [],
    publishEffectiveFrom: null,
    model: {
      id: null,
      name: '',
      description: '',
      is_default: false
    },
    version: {
      id: null,
      version: 1,
      rules: [
        { scope_type: 'BASE', pricing_mode: 'FLAT', fee_percent: 10, fee_fixed: 0, tiers: [] }
      ]
    },
    simulator: {
      amount: 100,
      product_type: null,
      period_gmv: 0,
      period_sales: 0,
      result: null
    }
  }),

  computed: {
    scopeOptions() {
      return [
        { value: 'BASE', text: this.$t('fee_plans.scope_.BASE') },
        { value: 'PRODUCT_TYPE', text: this.$t('fee_plans.scope_.PRODUCT_TYPE') },
        { value: 'MODALITY', text: this.$t('fee_plans.scope_.MODALITY') },
        { value: 'PRODUCT', text: this.$t('fee_plans.scope_.PRODUCT') }
      ]
    },
    productTypeOptions() {
      return Object.entries(PRODUCT_TYPE_LABELS).map(([value, text]) => ({ value, text }))
    },
    simulatorTypeOptions() {
      return [{ value: null, text: this.$t('fee_plans.simulator_any_type') }, ...this.productTypeOptions]
    },
    pricingModeOptions() {
      return [
        { value: 'FLAT', text: this.$t('fee_plans.mode_flat') },
        { value: 'TIERED', text: this.$t('fee_plans.mode_tiered') }
      ]
    },
    // Preview em linguagem natural — é o texto que explica a régua ao
    // estabelecimento, no mesmo espírito do preview da política de cancelamento.
    sellerPreview() {
      const parts = []
      const describeRate = (percent, fixed) => {
        const chunks = []
        if (percent) chunks.push(`${percent}%`)
        if (fixed) chunks.push(currencyFormatter(fixed))
        return chunks.length ? chunks.join(' + ') : 'taxa zero'
      }
      this.version.rules.forEach(rule => {
        let subject
        switch (rule.scope_type) {
          case 'BASE': subject = 'Qualquer venda'; break
          case 'PRODUCT_TYPE': subject = `Vendas de ${(PRODUCT_TYPE_LABELS[rule.product_type] || '…').toLowerCase()}`; break
          case 'MODALITY': {
            const modality = this.modalityOptions.find(option => option.value === rule.modality_id)
            subject = `Vendas de ${modality ? modality.text : '…'}`
            break
          }
          case 'PRODUCT': {
            const product = this.productOptions.find(option => option.value === rule.product_id)
            subject = `O produto "${product ? product.text : '…'}"`
            break
          }
        }
        if (rule.pricing_mode === 'FLAT') {
          const rate = describeRate(rule.fee_percent, rule.fee_fixed)
          parts.push(rate === 'taxa zero'
            ? `${subject} não paga taxa.`
            : `${subject} paga ${rate} de taxa.`)
        } else {
          const steps = rule.tiers.map((tier, i) => {
            if (i === 0) return `começa em ${describeRate(tier.fee_percent, tier.fee_fixed)}`
            const conditions = []
            if (tier.min_gmv) conditions.push(`${currencyFormatter(tier.min_gmv)} vendidos`)
            if (tier.min_sales_count) conditions.push(`${tier.min_sales_count} vendas`)
            return `a partir de ${conditions.join(' e ')} no mês cai para ${describeRate(tier.fee_percent, tier.fee_fixed)}`
          })
          parts.push(`${subject}: ${steps.join('; ')}.`)
        }
      })
      return parts.join('\n')
    }
  },

  methods: {
    formatCents(cents) {
      return currencyFormatter((cents || 0) / 100)
    },
    addRule() {
      this.version.rules.push({
        scope_type: 'PRODUCT_TYPE',
        product_type: 'LES',
        modality_id: null,
        product_id: null,
        pricing_mode: 'FLAT',
        fee_percent: 10,
        fee_fixed: 0,
        tiers: []
      })
    },
    removeRule(i) {
      this.version.rules.splice(i, 1)
    },
    addTier(rule) {
      if (!rule.tiers.length) {
        // A primeira faixa é o piso (sem limiar); nasce com a taxa da regra.
        rule.tiers.push({ min_gmv: null, min_sales_count: null, fee_percent: rule.fee_percent || 10, fee_fixed: 0 })
      }
      rule.tiers.push({ min_gmv: null, min_sales_count: null, fee_percent: 0, fee_fixed: 0 })
    },
    versionPayload() {
      const toCents = value => (value ? Math.round(Number(value) * 100) : 0)
      return {
        rules: this.version.rules.map(rule => ({
          scope_type: rule.scope_type,
          product_type: rule.product_type || null,
          modality_id: rule.modality_id || null,
          product_id: rule.product_id || null,
          pricing_mode: rule.pricing_mode,
          fee_percent: rule.fee_percent || 0,
          fee_fixed_cents: toCents(rule.fee_fixed),
          tiers: rule.pricing_mode === 'TIERED'
            ? rule.tiers.map(tier => ({
              min_gmv_cents: tier.min_gmv ? toCents(tier.min_gmv) : null,
              min_sales_count: tier.min_sales_count || null,
              fee_percent: tier.fee_percent || 0,
              fee_fixed_cents: toCents(tier.fee_fixed)
            }))
            : []
        }))
      }
    },
    savePlan() {
      const data = {
        id: this.model.id,
        name: this.model.name,
        description: this.model.description
      }
      return this.service.createOrUpdate(data).then(plan => {
        this.model.id = plan.id
        return plan
      })
    },
    saveDraft() {
      this.$vs.loading()
      this.savePlan()
        .then(() => this.service.saveVersion(this.model.id, this.versionPayload()))
        .then(draft => {
          this.version.id = draft.id
          this.version.version = draft.version
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          if (!this.id) {
            this.$router.push(`/financial/fee_plans/${this.model.id}/edit`)
          }
        })
        .catch(error => this.showErrors(null, error, this))
        .finally(() => { this.$vs.loading.close() })
    },
    publish() {
      const payload = this.publishEffectiveFrom ? { effective_from: this.publishEffectiveFrom } : {}
      this.$vs.loading()
      this.savePlan()
        .then(() => this.service.saveVersion(this.model.id, this.versionPayload()))
        .then(draft => this.versionService.publish(draft.id, payload))
        .then(() => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          this.$router.push('/financial/fee_plans')
        })
        .catch(error => this.showErrors(null, error, this))
        .finally(() => { this.$vs.loading.close() })
    },
    simulate() {
      // Simula contra o RASCUNHO em edição: salva silenciosamente antes quando
      // já existe plano; num plano ainda não salvo, o simulador fica inerte.
      if (!this.model.id) return
      this.service.saveVersion(this.model.id, this.versionPayload())
        .then(draft => this.service.simulate({
          version_id: draft.id,
          amount_cents: Math.round((this.simulator.amount || 0) * 100),
          product_type: this.simulator.product_type,
          period_gmv_cents: Math.round((this.simulator.period_gmv || 0) * 100),
          period_sales_count: this.simulator.period_sales || 0
        }))
        .then(result => { this.simulator.result = result })
        .catch(() => {})
    },
    loadModalities() {
      this.modalityService.get('/all').then(response => {
        this.modalityOptions = (Array.isArray(response) ? response : (response.data || [])).map(modality => ({
          value: modality.id,
          text: modality.name
        }))
      }).catch(() => {})
    },
    loadProducts() {
      this.productService.grid('', { page: 1, per_page: 100 }).then(response => {
        this.productOptions = this._.get(response, 'model.data', []).map(product => ({
          value: product.id,
          text: product.name
        }))
      }).catch(() => {})
    },
    loadData(id) {
      this.$vs.loading()
      Promise.all([
        this.service.read(id),
        this.service.getVersions(id)
      ]).then(([plan, versions]) => {
        this.model.id = plan.id
        this.model.name = plan.name
        this.model.description = plan.description
        this.model.is_default = !!plan.is_default

        // Edita o rascunho se existir; senão parte da vigente para a próxima versão
        const draft = versions.find(version => version.status === 'DR')
        const active = versions.find(version => version.status === 'AC')
        const base = draft || active

        if (base) {
          this.version.id = draft ? draft.id : null
          this.version.version = draft ? draft.version : ((active ? active.version : 0) + 1)
          this.version.rules = (base.rules || []).map(rule => ({
            scope_type: rule.scope_type,
            product_type: rule.product_type,
            modality_id: rule.modality_id,
            product_id: rule.product_id,
            pricing_mode: rule.pricing_mode,
            fee_percent: rule.fee_percent,
            fee_fixed: (rule.fee_fixed_cents || 0) / 100,
            tiers: (rule.tiers || []).map(tier => ({
              min_gmv: tier.min_gmv_cents !== null ? tier.min_gmv_cents / 100 : null,
              min_sales_count: tier.min_sales_count,
              fee_percent: tier.fee_percent,
              fee_fixed: (tier.fee_fixed_cents || 0) / 100
            }))
          }))
        }
        this.simulate()
      }).finally(() => { this.$vs.loading.close() })
    }
  },

  beforeMount() {
    this.service = FeePlanService.build(this.$vs)
    this.versionService = FeePlanVersionService.build(this.$vs)
    this.modalityService = ModalityService.build(this.$vs)
    this.productService = ProductService.build(this.$vs)
  },

  mounted() {
    this.loadModalities()
    this.loadProducts()
    if (this.id) {
      this.loadData(this.id)
    }
  }
}
</script>

<style lang="scss" scoped>
.rule-row {
  border: 1px solid #e4e4e4;
}
</style>
